// Dashboard.jsx
// Updated Free Tools Dashboard:
// - Financial Runway Calculator
// - AI Career Risk Calculator
// - Snapshot / Deep Audit upgrade flow
// - Saved Articles
// - Existing questionnaire / premium logic preserved

import { useEffect, useState } from "react";
import {
  useNavigate,
  Link,
} from "react-router-dom";
import logo from "../assets/logo_basic.png";
import "../Dashboard.css";
import { supabase } from "../supabaseClient";
import {
  Menu,
  X,
} from "lucide-react";
import { useCookieConsent } from "../context/CookieConsentProvider";
import {
  getMyCareerResilienceScore,
} from "../api/careerResilienceScoreApi";

export default function Dashboard() {
  const { openPreferences } =
    useCookieConsent();

  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(true);
  const [user, setUser] =
    useState({
      name: "",
      email: "",
      isPremium: false,
      plan: "snapshot",
      source: "career_audit",
    });

  const [
    displayName,
    setDisplayName,
  ] = useState("");

  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);

  // Snapshot choice modal for Free Tools users
  const [
    snapshotChoiceOpen,
    setSnapshotChoiceOpen,
  ] = useState(false);

  // Saved Blog Articles
  const [
    savedArticles,
    setSavedArticles,
  ] = useState([]);

  // Career Resilience Score profile
  const [
    rcrsProfile,
    setRcrsProfile,
  ] = useState(null);

  const [
    rcrsLoading,
    setRcrsLoading,
  ] = useState(true);

  const [
    rcrsError,
    setRcrsError,
  ] = useState("");

  const t = (key) => {
    const map = {
      "dashboard.nav.home":
        "Home",

      "dashboard.nav.about":
        "About",

      "dashboard.nav.contact":
        "Contact",

      "dashboard.nav.dashboard":
        "Dashboard",
    };

    return map[key] || key;
  };

  // ==================================================
  // LOAD USER
  // ==================================================

  useEffect(() => {
    const loadUser = async () => {
      try {
        setLoading(true);

        const {
          data: authData,
        } =
          await supabase.auth
            .getUser();

        const authUser =
          authData?.user;

        if (!authUser) {
          navigate(
            "/choose-plan",
            {
              replace: true,
            }
          );

          return;
        }

        const authUid =
          authUser.id;

        const {
          data: dbUser,
          error: dbUserError,
        } =
          await supabase
            .from("users")
            .select(
              "name, email, is_premium, plan, source"
            )
            .eq(
              "auth_uid",
              authUid
            )
            .maybeSingle();

        if (dbUserError) {
          console.warn(
            "Dashboard user lookup warning:",
            dbUserError.message
          );
        }

        const name =
          dbUser?.name ||
          authUser
            ?.user_metadata
            ?.full_name ||
          authUser
            ?.user_metadata
            ?.name ||
          "";

        const email =
          dbUser?.email ||
          authUser?.email ||
          "";

        const isPremium =
          dbUser?.is_premium ===
          true;

        const userPlanRaw =
          dbUser?.plan ||
          localStorage.getItem(
            `user_plan_${authUid}`
          ) ||
          sessionStorage.getItem(
            "user_plan"
          ) ||
          localStorage.getItem(
            "selectedPlan"
          ) ||
          "";

        const userPlan =
          userPlanRaw
            .toString()
            .trim()
            .toLowerCase();

        const sourceRaw =
          dbUser?.source ||
          localStorage.getItem(
            "registrationSource"
          ) ||
          "";

        const source =
          sourceRaw
            .toString()
            .trim()
            .toLowerCase();

        setUser({
          name,
          email,
          isPremium,
          plan: userPlan,
          source,
        });

        setDisplayName(name);
      } catch (e) {
        console.warn(
          "Dashboard loadUser error:",
          e?.message || e
        );
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [navigate]);

  // ==================================================
  // LOAD SAVED ARTICLES
  // ==================================================
  const loadSavedArticles =
    async () => {
      try {
        const {
          data: sessionData,
        } =
          await supabase.auth
            .getSession();

        const authUser =
          sessionData?.session
            ?.user;

        if (!authUser) {
          setSavedArticles(
            []
          );

          return;
        }

        const {
          data,
          error,
        } =
          await supabase
            .from(
              "saved_articles"
            )
            .select(`
              id,
              created_at,
              blog_posts (
                id,
                title,
                slug,
                excerpt
              )
            `)
            .eq(
              "user_id",
              authUser.id
            )
            .order(
              "created_at",
              {
                ascending:
                  false,
              }
            );

        if (error) {
          console.error(
            "Saved articles load error:",
            error
          );

          setSavedArticles(
            []
          );

          return;
        }

        setSavedArticles(
          data || []
        );
      } catch (err) {
        console.error(
          "Saved articles error:",
          err
        );

        setSavedArticles(
          []
        );
      }
    };

  useEffect(() => {
    loadSavedArticles();
  }, []);

  // ==================================================
  // LOAD CAREER RESILIENCE SCORE
  // ==================================================

  const loadCareerResilienceScore =
    async () => {
      try {
        setRcrsLoading(true);
        setRcrsError("");

        const profile =
          await getMyCareerResilienceScore();

        setRcrsProfile(profile);
      } catch (error) {
        console.error(
          "RCRS dashboard load error:",
          error
        );

        setRcrsError(
          error?.message ||
            "Unable to load your Career Resilience Score."
        );
      } finally {
        setRcrsLoading(false);
      }
    };

  useEffect(() => {
    loadCareerResilienceScore();
  }, []);

  // ==================================================
  // QUESTIONNAIRE HELPERS
  // ==================================================

  const getQuestionKey = (
    step,
    subStep,
    isPremium
  ) => {
    const stepConfig = {
      1: [
        "roleIndustry",
        "locationAge",
      ],

      2: [
        "experience",
      ],

      3: [
        "education",
        "technical",
        "soft",
      ],

      4: isPremium
        ? [
            "workPreference",
            "onet",
          ]
        : [
            "workPreference",
          ],

      5: [
        "financial",
        "ai",
      ],

      6: [
        "done",
      ],
    };

    const arr =
      stepConfig[
        Number(step)
      ] || [];

    return (
      arr[
        Number(subStep)
      ] || null
    );
  };

  const markExitBeforeLogout =
    async (
      uid,
      isPremium
    ) => {
      try {
        const assessmentId =
          localStorage.getItem(
            `current_assessment_id_${uid}`
          );

        if (!assessmentId) {
          return;
        }

        const savedStep =
          Number(
            localStorage.getItem(
              `questionnaire_step_${uid}`
            ) || 1
          );

        const savedSubStep =
          Number(
            localStorage.getItem(
              `questionnaire_substep_${uid}`
            ) || 0
          );

        const lastAnsweredQuestionKey =
          localStorage.getItem(
            `questionnaire_last_answered_${uid}`
          ) || null;

        const currentQuestionKey =
          getQuestionKey(
            savedStep,
            savedSubStep,
            isPremium
          );

        const {
          data: sessionData,
        } =
          await supabase.auth
            .getSession();

        const token =
          sessionData
            ?.session
            ?.access_token;

        if (!token) {
          return;
        }

        await fetch(
          "https://api.reignitespirit.com/api/careerpivot/mark-exit",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Authorization:
                `Bearer ${token}`,
            },

            body:
              JSON.stringify({
                assessmentId,
                step:
                  savedStep,
                subStep:
                  savedSubStep,
                currentQuestionKey,
                lastAnsweredQuestionKey,
                questionnaireStatus:
                  "logged_out_midway",
                exitReason:
                  "logout",
              }),
          }
        );
      } catch (e) {
        console.warn(
          "markExitBeforeLogout failed:",
          e?.message || e
        );
      }
    };

  // ==================================================
  // LOGOUT
  // ==================================================

  const handleLogout =
    async () => {
      try {
        const {
          data: authData,
        } =
          await supabase.auth
            .getUser();

        const authUser =
          authData?.user;

        const uid =
          authUser?.id ||
          sessionStorage.getItem(
            "auth_uid"
          );

        if (uid) {
          await markExitBeforeLogout(
            uid,
            user?.isPremium ===
              true
          );

          const keys = [
            `dashboard_step_${uid}`,
            `current_assessment_id_${uid}`,
            `payment_assessment_id_${uid}`,
            `payment_started_${uid}`,
            `payment_handled_${uid}`,
            `results_lock_${uid}`,
            `consentAccepted_${uid}`,
            `resume_results_once_${uid}`,
            `questionnaire_form_${uid}`,
            `questionnaire_step_${uid}`,
            `questionnaire_substep_${uid}`,
            `questionnaire_last_answered_${uid}`,
          ];

          keys.forEach(
            (k) => {
              localStorage.removeItem(
                k
              );

              sessionStorage.removeItem(
                k
              );
            }
          );

          Object.keys(
            localStorage
          ).forEach((k) => {
            if (
              k.includes(uid)
            ) {
              localStorage.removeItem(
                k
              );
            }
          });

          Object.keys(
            sessionStorage
          ).forEach((k) => {
            if (
              k.includes(uid)
            ) {
              sessionStorage.removeItem(
                k
              );
            }
          });
        }

        [
          "dashboard_step",
          "current_assessment_id",
          "payment_assessment_id",
          "consentAccepted",
          "force_results",
          "lemon_success",
          "report_generation_started",
          "redirect_after_login",
          "selectedPlan",

          // New Free Tools values
          "registrationSource",
          "freeToolIntent",

          "userId",
          "userEmail",
          "userName",
          "isPremium",
        ].forEach((k) =>
          localStorage.removeItem(
            k
          )
        );

        sessionStorage.removeItem(
          "auth_uid"
        );

        sessionStorage.removeItem(
          "user_plan"
        );

        sessionStorage.removeItem(
          "db_verified"
        );

        await supabase.auth
          .signOut();
      } catch (e) {
        console.warn(
          "logout error:",
          e?.message || e
        );
      } finally {
        window.location.href =
          "/choose-plan";
      }
    };

  // ==================================================
  // PLAN / USER TYPE
  // ==================================================

  const isFreeCalculatorUser =
    user?.plan ===
      "free_calculator" ||
    user?.plan ===
      "calculator" ||
    user?.plan ===
      "free" ||

    // Existing Financial Calculator source
    user?.source ===
      "financial_calculator" ||

    // NEW AI Risk Calculator source
    user?.source ===
      "ai_risk_calculator" ||

    // Existing blog flow
    user?.source ===
      "blog_comment";

  /*
    Future-friendly alias.

    Existing code can continue using
    isFreeCalculatorUser, but conceptually
    the account now has access to multiple
    Free Tools.
  */
  const isFreeToolsUser =
    isFreeCalculatorUser;

  const planLabel =
    isFreeToolsUser
      ? "FREE TOOLS"
      : user?.isPremium
      ? "DEEP DIVE"
      : "SNAPSHOT";

      // ==================================================
// QUESTIONNAIRE BUTTON LABEL
// ==================================================

const questionnaireButtonLabel = (() => {
  const currentPlan = String(
    user?.plan || ""
  )
    .trim()
    .toLowerCase();

  if (
    currentPlan === "deep_audit" ||
    currentPlan === "deep_dive"
  ) {
    return "✅ Open Deep Dive Questionnaire";
  }

  if (
    currentPlan === "snapshot"
  ) {
    return "✅ Open Snapshot Questionnaire";
  }

  return "✅ Open Questionnaire";
})();
  // ==================================================
  // DASHBOARD COPY
  // ==================================================

  const dashboardCopy =
    isFreeToolsUser
      ? {
          badge:
            "FREE TOOLS DASHBOARD",

          titleLine2:
            "Your Career Resilience Hub",

          lead:
            "Use your free tools to understand career risk, financial readiness and your next best action.",

          bullets: [
            "Complete the AI Career Risk Calculator to generate your Reignite Career Resilience Score.",

            "Complete the Financial Runway Calculator to understand your transition readiness.",

            "Return here anytime to review your score, progress and recommended next action.",
          ],

          instructionLabel:
            "",
        }
      : {
          badge:
            `${planLabel} DASHBOARD`,

          titleLine2:
            "Welcome to your Dashboard",

          lead:
            "This is your central workspace for managing your AI Career Pivot journey.",

          bullets: [
            "Start or continue your personalized questionnaire to generate your AI-powered career strategy.",

            "Access your tools and premium features directly from the Quick Actions section below.",

            "When you're ready, take the next step toward your future-proof career path.",
          ],

          instructionLabel:
            "Instructions",
        };

  // ==================================================
  // UPGRADE FREE USER
  // ==================================================

  const upgradePlan =
    async (plan) => {
      try {
        const { data } =
          await supabase.auth
            .getSession();

        const token =
          data?.session
            ?.access_token;

        if (!token) {
          alert(
            "Session expired. Please login again."
          );

          navigate(
            "/login"
          );

          return;
        }

        const res =
          await fetch(
            "https://api.reignitespirit.com/api/users/upgrade-plan",
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify({
                  plan,
                }),
            }
          );

        if (!res.ok) {
          const msg =
            await res.text();

          throw new Error(
            msg ||
              "Plan update failed"
          );
        }

        localStorage.setItem(
          "selectedPlan",
          plan
        );

        localStorage.setItem(
          "registrationSource",
          "career_audit"
        );

        localStorage.removeItem(
          "freeToolIntent"
        );

        sessionStorage.setItem(
          "user_plan",
          plan
        );

        setUser(
          (prev) => ({
            ...prev,

            plan,

            source:
              "career_audit",

            isPremium:
              plan ===
              "deep_audit",
          })
        );

        navigate(
          `/questionnaire?plan=${plan}`
        );
      } catch (err) {
        console.error(
          "upgradePlan failed:",
          err
        );

        alert(
          err.message ||
            "Unable to update plan"
        );
      }
    };

  // ==================================================
  // FREE TOOL COMPLETION STATE
  // ==================================================

  const aiRiskCompleted =
    rcrsProfile?.rcrs?.aiAssessmentCompleted === true;

  const financialRunwayCompleted =
    rcrsProfile?.rcrs?.financialAssessmentCompleted === true;

  // ==================================================
  // LOADING
  // ==================================================

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f0a24] text-[#EB6AB5] font-audiowide text-lg">
        Loading your
        dashboard…
      </div>
    );
  }

  // ==================================================
  // UI
  // ==================================================

  return (
    <div className="app-shell bg-[#11111f] text-white font-sans overflow-x-hidden">

      {/* ============================================= */}
      {/* HEADER */}
      {/* ============================================= */}

      <header
        className="
          app-header
          w-full
          flex
          justify-between
          items-center
          px-4
          sm:px-8
          py-4
          bg-[#11111f]/90
          border-b
          border-[#EB6AB5]/20
          backdrop-blur-lg
          shadow-md
          relative
        "
      >
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() =>
  navigate(
    "/landing"
  )
}
        >
          <img
            src={logo}
            alt="Reignite Spirit Logo"
            className="w-12 sm:w-16 h-12 sm:h-16 drop-shadow-[0_0_14px_#eb6fbd]"
          />

          <h1 className="text-lg sm:text-2xl font-semibold tracking-wide text-[#EB6AB5]">
            REIGNITE.SPIRIT
          </h1>
        </div>

        {/* Desktop Navigation */}

        <nav className="hidden md:flex items-center gap-8 text-[#EB6AB5]/90 text-sm font-medium">

          {[
            "home",
            "about",
            "contact",
          ].map(
            (key) => (
              <button
                key={key}
                type="button"
               onClick={() => {
  if (
    key ===
    "home"
  ) {
    navigate(
      "/landing"
    );

    return;
  }

  if (
    key ===
    "about"
  ) {
    navigate(
      "/foundation-truth"
    );

    return;
  }

  navigate(
    `/${key}`
  );
}}
                className="hover:text-[#EB6AB5] transition"
              >
                {t(
                  `dashboard.nav.${key}`
                )}
              </button>
            )
          )}

          <button
            type="button"
            onClick={
              openPreferences
            }
            className="hover:text-[#EB6AB5] transition underline underline-offset-4"
          >
            Cookie Settings
          </button>

          <button
            type="button"
            className="bg-[#EB6AB5] text-[#0F0A24] font-semibold px-5 py-2 rounded-full hover:bg-[#ff83d1] transition"
            onClick={() =>
              navigate(
                "/dashboard"
              )
            }
          >
            {t(
              "dashboard.nav.dashboard"
            )}
          </button>
        </nav>

        {/* Mobile Hamburger */}

        <button
          type="button"
          onClick={() =>
            setMenuOpen(
              !menuOpen
            )
          }
          className="md:hidden p-2 rounded-lg text-[#EB6AB5] hover:bg-[#1d1632] transition"
          aria-label={
            menuOpen
              ? "Close menu"
              : "Open menu"
          }
        >
          {menuOpen ? (
            <X
              size={24}
            />
          ) : (
            <Menu
              size={24}
            />
          )}
        </button>

        {/* Mobile Menu */}

        {menuOpen && (
          <div className="absolute top-full right-4 mt-3 bg-[#11111f]/95 border border-[#EB6AB5]/30 rounded-xl shadow-lg p-5 flex flex-col gap-3 text-sm text-[#EB6AB5]/90 md:hidden z-[70]">

            {[
              "home",
              "about",
              "contact",
            ].map(
              (key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
  setMenuOpen(
    false
  );

  if (
    key ===
    "home"
  ) {
    navigate(
      "/landing"
    );

    return;
  }

  if (
    key ===
    "about"
  ) {
    navigate(
      "/foundation-truth"
    );

    return;
  }

  navigate(
    `/${key}`
  );
}}
                  className="text-left"
                >
                  {t(
                    `dashboard.nav.${key}`
                  )}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() => {
                setMenuOpen(
                  false
                );

                openPreferences();
              }}
              className="text-left underline underline-offset-4"
            >
              Cookie Settings
            </button>

            <button
              type="button"
              onClick={() => {
                setMenuOpen(
                  false
                );

                navigate(
                  "/dashboard"
                );
              }}
              className="bg-[#EB6AB5] text-[#0F0A24] font-semibold px-4 py-2 rounded-full hover:bg-[#ff83d1] transition w-full"
            >
              {t(
                "dashboard.nav.dashboard"
              )}
            </button>
          </div>
        )}
      </header>

      {/* ============================================= */}
      {/* MAIN */}
      {/* ============================================= */}

      <main className="app-body px-4 sm:px-6 lg:px-10 pt-6">

        <div className="max-w-6xl mx-auto">

          {/* ========================================= */}
          {/* DASHBOARD HERO */}
          {/* ========================================= */}

          <section className="w-full rounded-2xl border border-[#EB6AB5]/20 bg-[#0f0f1b]/30 backdrop-blur-sm overflow-hidden flex flex-col">

            <div className="px-4 sm:px-6 py-6 space-y-6 text-center lg:text-left">

              <p className="text-[#eb6fbd] font-semibold text-xs sm:text-sm uppercase tracking-[0.22em]">
                {
                  dashboardCopy.badge
                }
              </p>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight">

                <span className="text-[#eb6fbd]/90">
                  Hi{" "}
                  {(
                    displayName ||
                    user?.name ||
                    ""
                  )}{" "}
                  👋
                </span>

                <span className="block mt-2 text-white">
                  {
                    dashboardCopy.titleLine2
                  }
                </span>
              </h2>

              {dashboardCopy.lead && (
                <p className="text-sm sm:text-base text-white/85 leading-relaxed max-w-3xl mx-auto lg:mx-0">
                  {
                    dashboardCopy.lead
                  }
                </p>
              )}

              <ul className="space-y-3 text-sm sm:text-base text-white/85 leading-relaxed max-w-3xl mx-auto lg:mx-0">

                {dashboardCopy.bullets.map(
                  (
                    bullet,
                    idx
                  ) => (
                    <li
                      key={idx}
                      className="flex gap-3"
                    >
                      <span className="mt-2 h-2 w-2 rounded-full bg-[#eb6fbd] flex-shrink-0" />

                      <span>
                        {!isFreeToolsUser &&
                        idx ===
                          1 ? (
                          <>
                            {
                              bullet
                            }{" "}

                            <Link
                              to="/instructions"
                              className="text-[#eb6fbd] underline underline-offset-4 hover:text-[#ff83d1] transition"
                            >
                              {
                                dashboardCopy.instructionLabel
                              }
                            </Link>
                            .
                          </>
                        ) : (
                          bullet
                        )}
                      </span>
                    </li>
                  )
                )}
              </ul>
            </div>

            {/* ======================================= */}
            {/* CAREER RESILIENCE SCORE */}
            {/* ======================================= */}

            <CareerResilienceScoreCard
              profile={rcrsProfile}
              loading={rcrsLoading}
              error={rcrsError}
              onNavigate={navigate}
            />

            {/* ======================================= */}
            {/* QUICK ACTIONS */}
            {/* ======================================= */}

            <div className="border-t border-[#EB6AB5]/20 bg-[#11111f]/95 backdrop-blur px-4 sm:px-6 py-5">

              <div className="flex flex-col gap-3">

                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#EB6AB5]/80">
                  Quick Actions
                </p>

                <div className="flex flex-col sm:flex-row flex-wrap gap-3">

                  {/* Financial Calculator */}

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        "/runway-calculator"
                      )
                    }
                    className="
                      w-full
                      sm:w-auto
                      px-6
                      py-3
                      bg-gradient-to-r
                      from-[#EB6AB5]
                      to-[#ff83d1]
                      rounded-full
                      font-semibold
                      text-[#0F0A24]
                      hover:scale-105
                      transition
                      shadow-lg
                      whitespace-nowrap
                    "
                  >
                    {financialRunwayCompleted
                      ? "💰 Recalculate Financial Runway"
                      : "💰 Open Financial Runway Calculator"}
                  </button>

                  {/* AI Career Risk Calculator */}

                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        "/career-resilience-audit"
                      )
                    }
                    className="
                      w-full
                      sm:w-auto
                      px-6
                      py-3
                      bg-gradient-to-r
                      from-[#cb01bd]
                      to-[#EB6AB5]
                      rounded-full
                      font-semibold
                      text-white
                      hover:scale-105
                      transition
                      shadow-lg
                      shadow-[#cb01bd]/20
                      whitespace-nowrap
                    "
                  >
                    {aiRiskCompleted
                      ? "🤖 Retake AI Career Risk Assessment"
                      : "🤖 Open AI Career Risk Calculator"}
                  </button>

                  {/* Paid Plan Questionnaire */}

                  {/* Paid Plan Questionnaire */}

{!isFreeToolsUser && (
  <button
    type="button"
    onClick={() =>
      navigate(
        `/questionnaire?plan=${user?.plan || "snapshot"}`
      )
    }
    className="
      w-full
      sm:w-auto
      px-6
      py-3
      bg-gradient-to-r
      from-[#EB6AB5]
      to-[#ff83d1]
      rounded-full
      font-semibold
      text-[#0F0A24]
      hover:scale-105
      transition
      shadow-lg
      whitespace-nowrap
    "
  >
    {questionnaireButtonLabel}
  </button>
)}
                  {/* Premium Resume Optimizer */}

                  {user?.isPremium ===
                    true && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          "/resume-import"
                        )
                      }
                      className="
                        w-full
                        sm:w-auto
                        px-6
                        py-3
                        bg-gradient-to-r
                        from-[#EB6AB5]
                        to-[#ff83d1]
                        rounded-full
                        font-semibold
                        text-[#0F0A24]
                        hover:scale-105
                        transition
                        shadow-lg
                        whitespace-nowrap
                      "
                    >
                      ⚡ Open Resume
                      Optimizer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ============================================= */}
        {/* FREE USER UPGRADE */}
        {/* ============================================= */}

        {isFreeToolsUser && (
          <section className="max-w-6xl mx-auto mt-6 rounded-2xl border border-[#EB6AB5]/25 bg-[#0f0f1b]/40 p-6 sm:p-8 text-center">

            <p className="text-[#EB6AB5] font-bold uppercase tracking-[0.2em] text-xs mb-2">
              Upgrade anytime
            </p>

            <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Ready to generate your AI Career Pivot Strategy report?
            </h3>

            <p className="text-white/75 max-w-2xl mx-auto mb-6 leading-relaxed">
              Your free tools help you understand your current position. Upgrade when you're ready for a personalized career strategy, localized career pathways and reskilling recommendations.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">

              <button
                type="button"
                onClick={() => setSnapshotChoiceOpen(true)}
                className="px-6 py-3 rounded-full border border-[#EB6AB5] text-[#EB6AB5] font-bold hover:bg-[#EB6AB5]/10 transition"
              >
                <span>Choose Snapshot Report</span>{" "}
                <span className="text-white/40 line-through font-medium">
                  US$9.99
                </span>{" "}
                <span className="text-emerald-300 font-extrabold">
                  FREE
                </span>
              </button>

              <button
                type="button"
                onClick={() => upgradePlan("deep_audit")}
                className="px-6 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold hover:bg-[#ff83d1] transition"
              >
                <span>Choose Deep Audit Report</span>{" "}
                <span className="text-[#0F0A24]/45 line-through font-medium">
                  US$19.99
                </span>{" "}
                <span className="font-extrabold">
                  FREE
                </span>
              </button>

            </div>

            <p className="mt-4 text-xs sm:text-sm text-emerald-300 font-semibold">
              Launch Special — Limited Time
            </p>
          </section>
        )}

        {/* ============================================= */}
        {/* SNAPSHOT CONFIRMATION / DEEP AUDIT UPSELL */}
        {/* ============================================= */}

        {snapshotChoiceOpen && (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center px-4 bg-black/75 backdrop-blur-sm"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setSnapshotChoiceOpen(false);
              }
            }}
            role="presentation"
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="snapshot-choice-title"
              className="w-full max-w-lg rounded-3xl border border-[#EB6AB5]/45 bg-[#171326] shadow-2xl shadow-black/50 overflow-hidden"
            >
              <div className="p-6 sm:p-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-amber-300/20 bg-amber-300/10 text-2xl">
                  ✨
                </div>

                <p className="mt-5 text-xs font-bold uppercase tracking-[0.22em] text-[#EB6AB5]">
                  Before you continue
                </p>

                <h3
                  id="snapshot-choice-title"
                  className="mt-2 text-2xl sm:text-3xl font-extrabold text-white"
                >
                  Try the Deep Audit instead
                </h3>

                <p className="mt-3 text-sm sm:text-base leading-relaxed text-white/70">
                  Use the Deep Audit instead. It&apos;s free for now and gives you a more detailed career strategy.
                </p>

                <div className="mt-7 space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSnapshotChoiceOpen(false);
                      upgradePlan("deep_audit");
                    }}
                    className="w-full rounded-xl bg-gradient-to-r from-[#EB6AB5] to-[#ff83d1] px-5 py-3.5 font-extrabold text-[#0F0A24] hover:scale-[1.01] transition"
                  >
                    Use Deep Audit →
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSnapshotChoiceOpen(false);
                      upgradePlan("snapshot");
                    }}
                    className="w-full rounded-xl border border-[#EB6AB5] bg-transparent px-5 py-3.5 font-bold text-[#EB6AB5] hover:bg-[#EB6AB5]/10 transition"
                  >
                    Continue with Snapshot
                  </button>

                  <button
                    type="button"
                    onClick={() => setSnapshotChoiceOpen(false)}
                    className="w-full px-5 py-2 text-sm font-medium text-white/50 hover:text-white/80 transition"
                  >
                    Close
                  </button>
                </div>

                <p className="mt-4 text-xs text-emerald-300/90 font-semibold">
                  Both plans are currently FREE during the launch special.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ============================================= */}
        {/* SAVED ARTICLES */}
        {/* ============================================= */}

        <section className="max-w-6xl mx-auto rounded-2xl border border-white/10 bg-white/5 p-6 mt-8">

          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">

            <h2 className="text-2xl font-bold text-[#EB6AB5]">
              Saved Articles
            </h2>

            <Link
              to="/blog"
              className="text-sm text-[#EB6AB5] underline underline-offset-4 hover:text-[#ff83d1]"
            >
              Browse Blog
            </Link>
          </div>

          {savedArticles.length ===
          0 ? (
            <p className="text-white/60">
              No saved articles yet.
            </p>
          ) : (
            <div className="space-y-4">

              {savedArticles.map(
                (item) => (
                  <Link
                    key={
                      item.id
                    }
                    to={`/blog/${item.blog_posts?.slug}`}
                    className="block rounded-xl bg-black/20 border border-white/10 p-4 hover:border-[#EB6AB5]/40 transition"
                  >
                    <h3 className="font-bold text-white">
                      {
                        item
                          .blog_posts
                          ?.title
                      }
                    </h3>

                    <p className="text-white/60 text-sm mt-1">
                      {
                        item
                          .blog_posts
                          ?.excerpt
                      }
                    </p>

                    <p className="text-xs text-white/35 mt-2">
                      Saved on{" "}

                      {item.created_at
                        ? new Date(
                            item.created_at
                          ).toLocaleDateString()
                        : "-"}
                    </p>
                  </Link>
                )
              )}
            </div>
          )}
        </section>

        {/* ============================================= */}
        {/* FOOTER */}
        {/* ============================================= */}

        <footer className="w-full text-center py-4 border-t border-[#EB6AB5]/20 bg-[#11111f]/90 text-sm text-gray-400 mt-6">

          <div className="flex justify-center gap-6 flex-wrap">

            <a
              href="/privacy-cookie"
              className="hover:text-[#EB6AB5] transition underline underline-offset-4"
            >
              Privacy &amp;
              Cookie Policy
            </a>

            <a
              href="/terms-conditions"
              className="hover:text-[#EB6AB5] transition underline underline-offset-4"
            >
              Terms &amp;
              Conditions
            </a>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            ©{" "}
            {new Date().getFullYear()}{" "}
            Reignite Spirit. All
            rights reserved.
          </p>
        </footer>
      </main>

      {/* ============================================= */}
      {/* LOGOUT */}
      {/* ============================================= */}

      <button
        type="button"
        onClick={
          handleLogout
        }
        className="
          fixed
          bottom-6
          right-6
          z-[9999]
          px-5
          py-3
          rounded-full
          bg-[#241a35]/90
          backdrop-blur
          border
          border-[#EB6AB5]/40
          text-[#EB6AB5]
          font-semibold
          shadow-xl
          hover:bg-[#332149]
          active:scale-95
          transition
        "
      >
        🚪 Logout
      </button>
    </div>
  );
}

function CareerResilienceScoreCard({
  profile,
  loading,
  error,
  onNavigate,
}) {
  // ==================================================
  // LOADING
  // ==================================================

  if (loading) {
    return (
      <div className="border-t border-[#EB6AB5]/20 bg-[#0f0f1b]/70 px-4 sm:px-6 py-6">
        <div className="rounded-2xl border border-[#EB6AB5]/20 bg-[#11111f]/80 p-6">
          <p className="text-white/60">
            Loading your Career Resilience Score...
          </p>
        </div>
      </div>
    );
  }

  // ==================================================
  // ERROR
  // ==================================================

  if (error) {
    return (
      <div className="border-t border-[#EB6AB5]/20 bg-[#0f0f1b]/70 px-4 sm:px-6 py-6">
        <div className="rounded-2xl border border-red-400/20 bg-red-500/5 p-6">
          <p className="text-red-300">
            {error}
          </p>
        </div>
      </div>
    );
  }

  // ==================================================
  // PROFILE STATE
  // ==================================================

  const rcrs = profile?.rcrs || null;

  const completion =
    profile?.completion || {
      aiRisk: false,
      financialRunway: false,
      skillGap: false,
      completedCount: 0,
      availableToolCount: 2,
      completionPercent: 0,
    };

  const nextAction =
    profile?.nextAction || null;

  /*
    IMPORTANT:
    profile.hasScore means a career_resilience_scores row exists.

    A row can exist because Financial Runway was completed first,
    so it must NOT be used to decide whether an AI RCRS exists.
  */
  const aiAssessed =
    rcrs?.aiAssessmentCompleted === true;

  const financialAssessed =
    rcrs?.financialAssessmentCompleted === true;

  const hasAnyAssessment =
    aiAssessed || financialAssessed;

  // ==================================================
  // NOTHING COMPLETED YET
  // ==================================================

  if (!profile?.hasScore && !hasAnyAssessment) {
    return (
      <div className="border-t border-[#EB6AB5]/20 bg-[#0f0f1b]/70 px-4 sm:px-6 py-6">
        <div className="rounded-3xl border border-[#EB6AB5]/25 bg-[#11111f]/85 p-6 sm:p-8 text-center">
          <p className="text-[#EB6AB5] text-xs font-bold uppercase tracking-[0.22em]">
            Career Resilience
          </p>

          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-white">
            Create Your Reignite Career Resilience Score
          </h2>

          <p className="mt-3 text-white/65 max-w-2xl mx-auto">
            Complete the AI Career Risk Calculator to generate your first
            resilience score and understand how prepared your career is for
            AI-driven change.
          </p>

          <button
            type="button"
            onClick={() =>
              onNavigate(
                "/career-resilience-audit"
              )
            }
            className="mt-6 px-7 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold hover:bg-[#ff83d1] transition"
          >
            Create My Score →
          </button>
        </div>
      </div>
    );
  }

  // ==================================================
  // MAIN PROFILE CARD
  // ==================================================

  return (
    <div className="border-t border-[#EB6AB5]/20 bg-[#0f0f1b]/70 px-4 sm:px-6 py-6">
      <div className="rounded-3xl border border-[#EB6AB5]/30 bg-[#0f0f1b] overflow-hidden">

        {/* ========================================= */}
        {/* RCRS HEADER */}
        {/* ========================================= */}

        <div className="p-6 sm:p-8 text-center border-b border-white/10">
          <p className="text-[#EB6AB5] text-xs sm:text-sm font-bold uppercase tracking-[0.22em]">
            Reignite Career Resilience Score
          </p>

          {aiAssessed ? (
            <>
              <div className="mt-5 flex items-end justify-center gap-2">
                <span className="text-6xl sm:text-7xl font-black text-white">
                  {rcrs?.overallScore ?? 0}
                </span>

                <span className="text-white/40 text-xl pb-2">
                  /100
                </span>
              </div>

              <div className="mt-4">
                <span className="inline-flex px-5 py-2 rounded-full border border-[#EB6AB5]/30 bg-[#EB6AB5]/10 text-[#EB6AB5] font-bold">
                  {rcrs?.category || "Assessed"}
                </span>
              </div>

              <p className="mt-3 text-xs text-white/35">
                {rcrs?.scoreVersion || "RCRS_V1"}
              </p>
            </>
          ) : (
            <div className="mt-6">
              <div className="inline-flex items-center justify-center rounded-full border border-[#EB6AB5]/30 bg-[#EB6AB5]/10 px-5 py-2 text-[#EB6AB5] font-bold">
                Not Yet Assessed
              </div>

              <h2 className="mt-5 text-2xl sm:text-3xl font-extrabold text-white">
                Generate Your Career Resilience Score
              </h2>

              <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed text-white/60">
                Complete the AI Career Risk Calculator to generate your first
                Reignite Career Resilience Score and understand your resilience
                to AI-driven career change.
              </p>

              <button
                type="button"
                onClick={() =>
                  onNavigate(
                    "/career-resilience-audit"
                  )
                }
                className="mt-5 px-7 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold hover:bg-[#ff83d1] transition"
              >
                Start AI Career Risk Assessment →
              </button>
            </div>
          )}
        </div>

        {/* ========================================= */}
        {/* RESILIENCE DIMENSIONS - ONLY AFTER AI */}
        {/* ========================================= */}

        {aiAssessed && (
          <div className="p-6 sm:p-8">
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Your resilience dimensions
            </h3>

            <p className="text-white/55 text-sm mt-1">
              These dimensions explain what is strengthening or weakening your
              overall score.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
              <ResilienceMetric
                title="AI Resilience"
                value={rcrs?.aiResilienceScore}
              />

              <ResilienceMetric
                title="Future Demand"
                value={rcrs?.futureDemandScore}
              />

              <ResilienceMetric
                title="Human Advantage"
                value={rcrs?.humanAdvantageScore}
              />

              <ResilienceMetric
                title="Adaptability"
                value={rcrs?.adaptabilityScore}
              />

              <ResilienceMetric
                title="Skill Transferability"
                value={rcrs?.skillTransferabilityScore}
              />
            </div>
          </div>
        )}

        {/* ========================================= */}
        {/* FINANCIAL TRANSITION READINESS */}
        {/* ========================================= */}

        {financialAssessed && (
          <div
            className={`p-6 sm:p-8 ${
              aiAssessed
                ? "border-t border-white/10"
                : ""
            }`}
          >
            <div className="rounded-2xl border border-[#EB6AB5]/20 bg-[#EB6AB5]/5 p-5 sm:p-6">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#EB6AB5]">
                Transition Readiness
              </p>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <p className="text-white/55 text-xs">
                    Financial Readiness
                  </p>

                  <div className="mt-2 flex items-end gap-2">
                    <strong className="text-3xl text-white">
                      {rcrs?.financialReadinessScore ?? "—"}
                    </strong>

                    {rcrs?.financialReadinessScore != null && (
                      <span className="text-white/35 text-xs mb-1">
                        /100
                      </span>
                    )}
                  </div>

                  <p className="mt-2 text-sm text-[#EB6AB5] font-semibold">
                    Financial assessment complete
                  </p>
                </div>

                <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                  <p className="text-white/55 text-xs">
                    Financial Runway
                  </p>

                  <strong className="mt-2 block text-3xl text-white">
                    {rcrs?.financialRunwayMonths != null
                      ? `${Number(
                          rcrs.financialRunwayMonths
                        ).toFixed(1)} months`
                      : "—"}
                  </strong>

                  <p className="mt-2 text-sm text-white/50">
                    Estimated transition buffer
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-white/55">
                Financial Readiness complements your Career Resilience Score.
                It measures whether you have enough financial flexibility to
                manage a career transition and does not change the core RCRS.
              </p>
            </div>
          </div>
        )}

        {/* ========================================= */}
        {/* FREE TOOL PROGRESS */}
        {/* ========================================= */}

        <div className="p-6 sm:p-8 border-t border-white/10">
          <div className="flex justify-between gap-4 items-center flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#EB6AB5]">
                Free Tool Progress
              </p>

              <h3 className="text-xl font-bold text-white mt-1">
                {completion?.completedCount ?? 0}
                {" of "}
                {completion?.availableToolCount ?? 2}
                {" completed"}
              </h3>
            </div>

            <strong className="text-[#EB6AB5]">
              {completion?.completionPercent ?? 0}%
            </strong>
          </div>

          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mt-4">
            <div
              className="h-full bg-[#EB6AB5] rounded-full transition-all duration-500"
              style={{
                width: `${completion?.completionPercent ?? 0}%`,
              }}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <CompletionItem
              complete={completion?.aiRisk === true}
              title="AI Career Risk Calculator"
            />

            <CompletionItem
              complete={completion?.financialRunway === true}
              title="Financial Runway Calculator"
            />
          </div>
        </div>

        {/* ========================================= */}
        {/* NEXT BEST ACTION */}
        {/* ========================================= */}

        {nextAction?.title && (
          <div className="p-6 sm:p-8 border-t border-[#EB6AB5]/20 bg-[#EB6AB5]/5">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#EB6AB5]">
              Next Best Action
            </p>

            <h3 className="text-xl sm:text-2xl font-bold text-white mt-2">
              {nextAction.title}
            </h3>

            {nextAction.description && (
              <p className="text-white/65 mt-2 max-w-2xl">
                {nextAction.description}
              </p>
            )}

            {nextAction.type === "profile_complete" && (
              <div className="mt-5 inline-flex rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-300">
                ✓ Current free assessments complete
              </div>
            )}

            {nextAction.route &&
              nextAction.type !== "profile_complete" && (
                <button
                  type="button"
                  onClick={() =>
                    onNavigate(
                      nextAction.route
                    )
                  }
                  className="mt-5 px-7 py-3 rounded-full bg-[#EB6AB5] text-[#0F0A24] font-bold hover:bg-[#ff83d1] transition"
                >
                  Continue →
                </button>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResilienceMetric({
  title,
  value,
}) {
  const numericValue =
    Number(value ?? 0);

  const normalizedValue =
    Math.max(
      0,
      Math.min(
        numericValue,
        100
      )
    );

  return (
    <article className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <p className="text-white/55 text-xs">
        {title}
      </p>

      <div className="mt-2 flex items-end gap-1">
        <strong className="text-2xl text-white">
          {numericValue}
        </strong>

        <span className="text-white/35 text-xs mb-1">
          /100
        </span>
      </div>

      <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#EB6AB5]"
          style={{
            width:
              `${normalizedValue}%`,
          }}
        />
      </div>
    </article>
  );
  
}

function CompletionItem({
  complete,
  title,
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        complete
          ? "border-emerald-400/20 bg-emerald-400/5"
          : "border-white/10 bg-white/5"
      }`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`w-7 h-7 flex items-center justify-center rounded-full text-sm ${
            complete
              ? "bg-emerald-400/15 text-emerald-300"
              : "bg-white/10 text-white/40"
          }`}
        >
          {complete ? "✓" : "○"}
        </span>

        <span
          className={
            complete
              ? "text-white font-semibold"
              : "text-white/60"
          }
        >
          {title}
        </span>
      </div>
    </div>
  );
}


